import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { differenceInDays, format } from 'date-fns';
import { useReporter } from '../../reporter';
import { useDeliveryDates, useSubscription } from '../../subscriptions';
import { useCurrentPet } from '../useCurrentPet';
import { Heading, SmolLink } from '../../atoms';
import { RedirectToLogin } from '../../router';
import { ApiResult } from '../../api';
import { EditDeliveryProtectedRoute } from '../EditDeliveryProtectedRoute';
import { DatePickerForm } from '../DatePickerForm';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '../../Toast';
import { useSessionCustomer } from '../../customer';
import { AccountHeader } from '../AccountHeader';
import { CALENDAR, ERROR_MESSAGE } from '../constants';
import { Footer, LoadingView } from '../../components';
import { FOOTER_CONTENT } from '../../testing/constants';
import { changeDelivery } from '../QuickSelectForm/events';
import {
  userUpdatesDeliveryDate,
  viewChangeDeliveryDateCalendar,
} from './events';
import { LinkContainer, FormWrapper } from './styles';

export const DatePickerContainer = ({
  shippingBoxSoon,
  datePickerForm,
  labelButtonChangeFrequency,
  changeFrequencyButtonTarget,
  links,
  logoUrl,
  logoSrText,
  title,
}) => {
  const reporter = useReporter();
  const { showToast } = useToast();
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { pet, isLoading: isPetLoading } = useCurrentPet();
  const {
    nextDeliveryDate,
    updateSubscription,
    subscription,
    isLoading: isSubscriptionLoading,
  } = useSubscription(pet?.id);
  const { disabledDates, minDate, maxDate } = useDeliveryDates(
    subscription?.id,
  );
  const {
    customer,
    isActive,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();

  const defaultValues = { next_delivery_target: nextDeliveryDate };

  const isLoading = isSubscriptionLoading || isCustomerLoading || isPetLoading;

  useEffect(() => {
    if (!isLoading) reporter.tag(viewChangeDeliveryDateCalendar());
  }, [reporter, isLoading]);

  if (!isActive && !isCustomerLoading) {
    return <RedirectToLogin />;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  const nextDeliveryLabel = `Next delivery for ${pet.name}`;
  const nextDeliveryDateFormatted = format(
    nextDeliveryDate,
    'EEEE, MMMM do, yyyy',
  );

  const getDaysChanged = newNextDeliveryDate =>
    differenceInDays(newNextDeliveryDate, nextDeliveryDate);

  const onSubmit = async form => {
    setIsSubmitting(true);
    const result = await updateSubscription({
      next_delivery_target: format(form.next_delivery_target, 'yyyy-MM-dd'),
    });
    ApiResult.match(result, {
      success: () => {
        reporter.tag(
          userUpdatesDeliveryDate({
            page: CALENDAR,
            days_changed: getDaysChanged(form.next_delivery_target),
            skipped: false,
            asap: false,
          }),
        );
        router.push('/account');
        showToast({
          status: 'success',
          message: 'Your delivery date has been successfully updated.',
          headline: 'Success',
        });
      },
      error: {
        server: () => {
          showToast({
            status: 'error',
            message: ERROR_MESSAGE,
            headline: 'Error Message',
          });
        },
        client: error => {
          reporter.error(error.original);
          showToast({
            status: 'error',
            message: TOAST_CLIENT_ERROR_MESSAGE,
            headline: 'Error Message',
          });
        },
      },
    });
    ApiResult.ifError(result, () => setTimeout(() => setIsSubmitting(false)));
  };

  const onChangeDateClick = () => {
    reporter.tag(changeDelivery());
  };

  return (
    <EditDeliveryProtectedRoute
      shippingBoxSoon={shippingBoxSoon}
      links={links}
      logoUrl={logoUrl}
      logoSrText={logoSrText}
    >
      <AccountHeader
        links={links}
        logoUrl={logoUrl}
        logoSrText={logoSrText}
        name={customer.first_name}
      />
      <FormWrapper>
        <Heading
          headingLevel="h1"
          typography="heading1"
          headingText={title}
          position="center"
        />
        <DatePickerForm
          {...datePickerForm}
          onSubmit={onSubmit}
          disabledDates={disabledDates}
          minDate={minDate}
          maxDate={maxDate}
          defaultValues={defaultValues}
          nextDeliveryLabel={nextDeliveryLabel}
          nextDeliveryDate={nextDeliveryDateFormatted}
          isSubmitting={isSubmitting}
        />
        <LinkContainer>
          <SmolLink
            href={{
              pathname: changeFrequencyButtonTarget,
              query: { pet_id: pet.id },
            }}
            onClick={onChangeDateClick}
          >
            {labelButtonChangeFrequency}
          </SmolLink>
        </LinkContainer>
      </FormWrapper>
      <Footer {...FOOTER_CONTENT} />
    </EditDeliveryProtectedRoute>
  );
};

DatePickerContainer.displayName = 'DatePickerContainer';

DatePickerContainer.propTypes = {
  shippingBoxSoon: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  datePickerForm: PropTypes.object.isRequired,
  labelButtonChangeFrequency: PropTypes.string.isRequired,
  changeFrequencyButtonTarget: PropTypes.string.isRequired,
  links: PropTypes.array.isRequired,
  logoUrl: PropTypes.string.isRequired,
  logoSrText: PropTypes.string.isRequired,
};
